import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material'
import { Autocomplete, Checkbox, Slider, TextField } from '@mui/material'
import { SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { StyledListItem } from '../../../styles/ProfileViewStyles'
import { User } from '../../../store/types'
import { ProjectDetails } from '../../../store/redux/reducers/projectsSlice'
import FilterMenu from '../FilterMenu'
import CollapsibleSection from '../../Button/CollapsibleSection'
import { getDurationString } from '../../../store/utils/worklogUtils'
import { Box } from '@mui/system'
import { CUTOFF } from '../../../store/constants/slider'
import { useLeftPadding } from '../../../store/redux/hooks'

export type ProjectContributionFilterType = {
  employees: User[]
  fromPercent: number
  toPercent: number
  workTimeRange: number | number[]
}

export const initialProjectContributionFilters: ProjectContributionFilterType = {
  employees: [],
  fromPercent: 0,
  toPercent: 0,
  workTimeRange: [],
}

const FilterProjectContributionMenu = ({
  setFilteringOptions,
  projectDetails,
}: {
  setFilteringOptions: React.Dispatch<React.SetStateAction<ProjectContributionFilterType>>
  projectDetails: ProjectDetails
}) => {
  const leftPadding = useLeftPadding()
  const { t } = useTranslation()
  const [selectedFilters, setSelectedFilters] = useState<ProjectContributionFilterType>(
    initialProjectContributionFilters
  )
  const [employeeName, setEmployeeName] = useState<User[]>([])
  const [employees, setEmployees] = useState<User[]>([])
  const [minHours, setMinHours] = useState<number>(24)
  const [maxHours, setMaxHours] = useState<number>(0)

  const sliderRangeMin = 30 * 60
  const sliderRangeMax = 12 * 60 * 60
  const [rangeSliderValue, setRangeSliderValue] = useState<number[]>([sliderRangeMin, sliderRangeMax])

  useEffect(() => {
    if (projectDetails && projectDetails.details) {
      const extractedEmployees = projectDetails.details.map(employeeDetail => employeeDetail.employee)
      setEmployees(extractedEmployees)
    }
    let min = 24
    let max = 0
    projectDetails.details.forEach(employeeDetail => {
      const { sumHours } = employeeDetail
      min = Math.min(sumHours, min)
      max = Math.max(sumHours, max)
    })
    setMinHours(min)
    setMaxHours(max)
  }, [projectDetails])

  useEffect(() => {
    setRangeSliderValue([minHours, maxHours])
  }, [minHours, maxHours])

  const handleSliderChange = (event: Event, newValue: number | number[]) => {
    setRangeSliderValue(newValue as number[])
    if (newValue) {
      setSelectedFilters(prev => ({
        ...prev,
        workTimeRange: newValue,
      }))
    }
  }

  const handleEmployeesChange = (event: SyntheticEvent<Element, Event>, value: User[]) => {
    if (value) {
      setSelectedFilters(prev => ({
        ...prev,
        employees: value,
      }))
      setEmployeeName(value)
    }
  }

  const handleApply = () => {
    setFilteringOptions(selectedFilters)
  }

  const handleClear = () => {
    resetSelectOptions()
    setRangeSliderValue([minHours, maxHours])
    setSelectedFilters(initialProjectContributionFilters)
    setFilteringOptions(initialProjectContributionFilters)
  }

  const resetSelectOptions = () => {
    setEmployeeName([])
  }

  const marks = [
    {
      value: minHours,
      label: getDurationString(minHours),
    },
    {
      value: maxHours,
      label: getDurationString(maxHours),
    },
  ]

  const icon = <CheckBoxOutlineBlank fontSize="small" />
  const checkedIcon = <CheckBox fontSize="small" />

  const calculateCutoff = useCallback(
    (value: number, cutoff: number) => {
      if (value === minHours) return cutoff
      if (value === maxHours) return -cutoff
      return 0
    },
    [minHours, maxHours]
  )

  const { cutoffLeft, cutoffRight } = useMemo(() => {
    return {
      cutoffLeft: calculateCutoff(rangeSliderValue[0], CUTOFF),
      cutoffRight: calculateCutoff(rangeSliderValue[1], -CUTOFF),
    }
  }, [rangeSliderValue, calculateCutoff])

  return (
    <FilterMenu filter={handleApply} clear={handleClear} fullHeight isLeftPadding={true}>
      <CollapsibleSection
        text={t('page.projects.detailsView.employee')}
        info={
          employeeName.length !== 0 ? (
            <Trans
              i18nKey="page.projects.filterPopup.leadersSelected"
              values={{
                numOfLeaders: employeeName.length,
              }}
            />
          ) : (
            ''
          )
        }
      >
        <Autocomplete
          sx={{ pl: leftPadding }}
          multiple
          id="employees-autocomplete"
          options={employees}
          getOptionLabel={option => `${option.firstName} ${option.lastName}`}
          disableCloseOnSelect
          value={employeeName}
          onChange={handleEmployeesChange}
          renderOption={(props, option, { selected }) => {
            const { key, ...rest } = props
            return (
              <StyledListItem key={key} {...rest}>
                <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                {`${option.firstName} ${option.lastName}`}
              </StyledListItem>
            )
          }}
          renderInput={params => (
            <TextField
              sx={{ maxWidth: '500px' }}
              size="small"
              fullWidth
              inputProps={{
                ...params.inputProps,
                'aria-label': t('page.projects.detailsView.employee') + '-autocomplete',
              }}
              InputProps={params.InputProps}
              label={t('page.projects.detailsView.employee')}
              placeholder={t('page.projects.detailsView.employee')}
            />
          )}
        />
      </CollapsibleSection>

      <CollapsibleSection text={t('page.projects.detailsView.workTime')}>
        <Box sx={{ pr: '10.5px', pl: `calc(${leftPadding} + 10.5px)`}}>
          <Slider
            sx={{
              width: '100%',
              '&.MuiSlider-root .MuiSlider-thumb[data-index="0"] .MuiSlider-valueLabel': {
                marginLeft: `${cutoffLeft}px`,
                '&::before': {
                  left: `calc(50% - ${cutoffLeft / 2}px)`,
                },
              },
              '&.MuiSlider-root .MuiSlider-thumb[data-index="1"] .MuiSlider-valueLabel': {
                marginRight: `${cutoffRight}px`,
                '&::before': {
                  left: `calc(50% + ${cutoffRight / 2}px)`,
                },
              },
            }}
            getAriaLabel={() => t('page.projects.detailsView.sliderLabel')}
            value={rangeSliderValue}
            onChange={handleSliderChange}
            min={minHours}
            max={maxHours}
            step={0.5}
            valueLabelFormat={x => getDurationString(x)}
            valueLabelDisplay="auto"
            marks={marks}
          />
        </Box>
      </CollapsibleSection>
    </FilterMenu>
  )
}

export default FilterProjectContributionMenu
